import { useDispatch, useSelector } from 'react-redux';

import UserPermissionOverrideService from '../services/UserPermissionOverrideService';
import useAlerts from './useAlerts';
import useLoading from './useLoading';
import {
  setUserPermissions,
  clearUserPermissions,
  toggleEffectivePermission,
  setEffectivePermissionList,
} from '../store/slices/userPermissionOverrides/userPermissionOverridesSlice';
import type { RootState, AppDispatch } from '../store/store';

/**
 * Panel administrativo permisos POR USUARIO (excepciones sobre el rol).
 *
 * El endpoint `/api/user-permissions` es solo-SUPPORT. A diferencia del panel
 * de roles, acá el frontend solo maneja `effectivePermissions` (un checkbox
 * por permiso, ya resuelto: base del rol + overrides) — el backend calcula
 * la diferencia contra `basePermissions` y guarda solo las excepciones.
 */
export const useUserPermissionOverrides = () => {
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const dispatch = useDispatch<AppDispatch>();

  const { selectedUser, basePermissions, effectivePermissions, catalog } = useSelector(
    (state: RootState) => state.userPermissionOverrides,
  );

  const getUserPermissions = async (userId: number) => {
    try {
      showLoadingIndicator();
      const result = await UserPermissionOverrideService.getUserPermissions(userId);

      const data = result?.data;
      if (!data || !Array.isArray(data.effectivePermissions)) {
        throw new Error('Respuesta invalida del servidor.');
      }

      dispatch(
        setUserPermissions({
          selectedUser: {
            id: data.userId,
            username: data.username,
            email: data.email,
            roles: data.roles ?? [],
          },
          basePermissions: data.basePermissions ?? [],
          effectivePermissions: data.effectivePermissions ?? [],
          catalog: data.catalog ?? [],
        }),
      );
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      showAlert('error', 'Error', 'No se pudieron cargar los permisos del usuario.');
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateUserPermissions = async () => {
    if (!selectedUser) return false;
    try {
      showLoadingIndicator();
      await UserPermissionOverrideService.updateUserPermissions(selectedUser.id, effectivePermissions);
      showAlert('success', 'Guardado', `Los permisos de ${selectedUser.username} se actualizaron.`);
      return true;
    } catch (error: any) {
      console.error('Error updating user permissions:', error);
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.title ??
        'No se pudieron actualizar los permisos.';
      showAlert('error', 'Error', msg);
      return false;
    } finally {
      hideLoadingIndicator();
    }
  };

  const togglePermission = (permission: string) => {
    dispatch(toggleEffectivePermission(permission));
  };

  const setPermissionList = (permissions: string[]) => {
    dispatch(setEffectivePermissionList(permissions));
  };

  const clearSelection = () => dispatch(clearUserPermissions());

  return {
    selectedUser,
    basePermissions,
    effectivePermissions,
    catalog,
    getUserPermissions,
    updateUserPermissions,
    togglePermission,
    setPermissionList,
    clearSelection,
  };
};

export default useUserPermissionOverrides;
